/**
 * SQLTab - Display generated SQL definition
 *
 * Shows the SQL query that represents the view with syntax highlighting
 */

import React, { useState } from 'react';
import { Code, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { View } from '@/types/views';

interface SQLTabProps {
  view: View;
}

/**
 * SQLTab Component
 */
export const SQLTab: React.FC<SQLTabProps> = ({ view }) => {
  const [copied, setCopied] = useState(false);

  /**
   * Handle copy to clipboard
   */
  const handleCopy = async () => {
    if (!view.sql_definition) return;

    try {
      await navigator.clipboard.writeText(view.sql_definition);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy SQL:', error);
    }
  };

  /**
   * No SQL available
   */
  if (!view.sql_definition) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-muted-foreground">
            <Code className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">SQL Not Available</p>
            <p className="text-sm">
              The generated SQL for this view is not available. It may need to be refreshed.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* SQL Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Code className="h-5 w-5" />
                Generated SQL
              </CardTitle>
              <CardDescription>
                DuckDB SQL query that defines this view
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleCopy}>
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-2 text-success" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy SQL
                </>
              )}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* SQL Display */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <pre className="bg-muted text-foreground p-4 rounded-lg overflow-x-auto text-sm font-mono">
              <code>{view.sql_definition}</code>
            </pre>
          </div>
        </CardContent>
      </Card>

      {/* SQL Info */}
      <Alert>
        <Code className="h-4 w-4" />
        <AlertDescription>
          This SQL query is automatically generated based on the view configuration. It represents
          how DuckDB will execute the view. For materialized views, this query is used to
          pre-compute and store results.
        </AlertDescription>
      </Alert>
    </div>
  );
};
